'use client'
import { ToDateTime, ValueWithUnit } from '@repo/shared/components'
import { useTranslate } from '@repo/shared/hooks'
import { UserOTCOrder, UserProject } from '@repo/services'
import {
  AspectRatio,
  Badge,
  Button,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  ModalTrigger,
  Row,
  Typography,
} from '@repo/ui/components'
// import { statusColorMapper, TStatus } from './balance.item'
import { useState } from 'react'
import Image from 'next/image'
import { globalConfig } from '@repo/shared/utils'

interface BalanceProjectDetailsDialogProps {
  data: UserOTCOrder
  children: React.ReactNode
  projectData: UserProject
}

const ImageComp = ({ src, isSquare }: { src?: string; isSquare?: boolean }) => {
  const url = src
    ? `${globalConfig.baseImageUrl}${src}`
    : '/images/placeholder.webp'

  return (
    <div className='w-lg'>
      <AspectRatio className=' aspect-1/1'>
        <Image
          src={isSquare ? '/images/club-gem.svg' : url}
          alt='Photo of property'
          fill
          sizes='(max-width: 768px) 100vw, 32px'
          className='h-full w-full  rounded-2xs object-cover'
          loading='lazy'
        />
      </AspectRatio>
    </div>
  )
}
export const BalanceProjectDetailsDialog = (
  props: BalanceProjectDetailsDialogProps,
) => {
  const { children } = props
  const {
    baseAmount,
    targetCurrency,
    targetAmount,
    charges,
    createdAt,
    orderType,
  } = props.data
  const { t } = useTranslate()

  const [open, setOpen] = useState(false)

  const title = orderType === 'buy' ? t('common_buy') : t('common_sell')

  const handleBack = () => {
    setOpen(false)
  }

  const totalCharges = charges?.reduce(
    (sum, charge) => sum + (charge.value ?? 0),
    0,
  )

  return (
    <Modal open={open} onOpenChange={setOpen}>
      <ModalTrigger>{children}</ModalTrigger>
      <ModalContent className='sm:max-w-[425px]'>
        <ModalHeader>
          <ModalTitle>
            <Badge
              color={orderType === 'buy' ? 'positive' : 'negative'}
              variant='solid'
            >
              {title}
            </Badge>
          </ModalTitle>
        </ModalHeader>

        <>
          <Row className='justify-center items-center'>
            <Typography size='sm' weight='bold'>
              {props.projectData.displayName}
            </Typography>
          </Row>
          <Row className='justify-between'>
            <Typography className='flex-1'>{t('common_time')}</Typography>
            <Typography className='text-neutral-surface-80' variant='bdi'>
              <ToDateTime date={new Date(Number(createdAt) * 1000)} />
            </Typography>
          </Row>
          <Row className='justify-between'>
            <Typography className='flex-1'>{t('common_token')}</Typography>

            <Row className='justify-between w-fit gap-3xs'>
              <Typography
                variant='bdi'
                size='sm'
                className='text-neutral-surface-100  font-[AzarMehr] '
              >
                {targetCurrency === 'MALEK' ? 'SQ²' : targetCurrency}
              </Typography>
              <ImageComp
                isSquare={targetCurrency === 'MALEK'}
                src={
                  targetCurrency === 'MALEK'
                    ? '/images/club-gem.svg'
                    : props.projectData?.logoURL
                }
              />
            </Row>
          </Row>

          <Row className='justify-between'>
            <Typography className='flex-1'>
              {t(
                orderType === 'buy'
                  ? 'common_details_buy_price'
                  : 'common_details_sell_price',
              )}
            </Typography>

            <ValueWithUnit
              value={
                targetAmount && baseAmount
                  ? Math.round(
                      (orderType === 'buy'
                        ? (baseAmount ?? 0) - (totalCharges ?? 0)
                        : (baseAmount ?? 0) + (totalCharges ?? 0)) /
                        targetAmount,
                    )
                  : 0
              }
              unit={'TMN'}
              scale={10}
              valueWeight='bold'
            />
          </Row>
          <Row className='justify-between'>
            <Typography className='flex-1'>
              {t(
                orderType === 'buy'
                  ? 'common_details_buy_amount'
                  : 'common_details_sell_amount',
              )}
            </Typography>
            <ValueWithUnit
              value={targetAmount ?? 0}
              unit={props.projectData.kind}
              scale={props.projectData.scale}
            />
          </Row>

          <Row className='justify-between'>
            <Typography className='flex-1'>
              {t(
                orderType === 'buy'
                  ? 'common_details_buy_total_amount'
                  : 'common_details_sell_total_amount',
              )}
            </Typography>

            <ValueWithUnit
              value={
                orderType === 'buy'
                  ? (baseAmount ?? 0) - (totalCharges ?? 0)
                  : (baseAmount ?? 0) + (totalCharges ?? 0)
              }
              unit={'TMN'}
              scale={10}
              valueWeight='bold'
            />
          </Row>
          <Row className='justify-between'>
            <Typography className='flex-1'>
              {t(
                orderType === 'buy'
                  ? 'common_details_fee'
                  : 'common_details_fee',
              )}
            </Typography>
            <ValueWithUnit
              value={totalCharges}
              unit={'TMN'}
              scale={10}
              valueWeight='bold'
            />
          </Row>
          <Row className='justify-between'>
            <Typography className='flex-1'>
              {t(
                orderType === 'buy'
                  ? 'common_details_buy_final_amount'
                  : 'common_details_sell_final_amount',
              )}
            </Typography>
            <ValueWithUnit
              value={baseAmount}
              unit={'TMN'}
              scale={10}
              valueWeight='bold'
            />
          </Row>
        </>

        <ModalFooter className='flex flex-col gap-md'>
          <Button
            variant='ghost'
            width='full'
            size='md'
            color='neutral'
            onClick={handleBack}
          >
            {t('common_back')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
